import { useCallback, useEffect, useRef, useState } from 'react'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import type { ShownFeedbackPrompt } from './components/feedback-dialog'
import type { BuddyResponseSummary } from './use-buddy-chat'

export const FEEDBACK_PROMPT_DELAY_MS = 2000

export interface FeedbackPromptGates {
  isPanelOpen: boolean
  isChatBusy: boolean
  isComposerEmpty: boolean
  // False once the thread is cleared, as New chat does.
  hasConversation: boolean
  // The manual /ticket flow: its dialog is open or its submit is in flight.
  isTicketFlowActive: boolean
  // PostHog isBuddyFeedbackDisabled, also true until the flags load: stops prompts
  // everywhere without a deploy.
  isBuddyFeedbackDisabled: boolean
}

// The single decision point for showing the prompt. How often a user is asked is capped
// server-side, which simply stops sending feedback_prompt.
const canOpen = (gates: FeedbackPromptGates): boolean =>
  gates.isPanelOpen &&
  !gates.isChatBusy &&
  gates.isComposerEmpty &&
  gates.hasConversation &&
  !gates.isTicketFlowActive &&
  !gates.isBuddyFeedbackDisabled

// Opens the feedback prompt a beat after a turn that asked for one. A gate already shut
// when the reply ends, or shutting while it waits (a new send, closing the panel, typing,
// New chat, opening /ticket), cancels it for good.
export const useFeedbackPrompt = (gates: FeedbackPromptGates) => {
  // Kept after close so the dialog does not blank while it animates out.
  const [prompt, setPrompt] = useState<ShownFeedbackPrompt | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const allowed = canOpen(gates)
  // Read by schedule and the timer, which must see the latest render rather than the one
  // that created them.
  const gatesRef = useRef(gates)
  gatesRef.current = gates

  const cancel = useCallback(() => clearTimeout(timerRef.current), [])

  useEffect(() => {
    if (!allowed) cancel()
  }, [allowed, cancel])

  useEffect(() => cancel, [cancel])

  // Runs from onFinish while the chat still reports busy, so `allowed` is already false and
  // the effect above cannot cancel for a gate shut mid-reply: check the others here too.
  const schedule = useCallback(
    ({ feedbackPrompt, sessionId }: BuddyResponseSummary) => {
      cancel()
      if (!feedbackPrompt || !sessionId) return
      if (!canOpen({ ...gatesRef.current, isChatBusy: false })) return
      timerRef.current = setTimeout(() => {
        if (!canOpen(gatesRef.current)) return
        setPrompt({ ...feedbackPrompt, sessionId, feedbackId: crypto.randomUUID() })
        setIsOpen(true)
        track(AnalyticsEvents.BUDDY_FEEDBACK_PROMPT_SHOWN, {
          reason: feedbackPrompt.reason,
          session_id: sessionId,
        })
      }, FEEDBACK_PROMPT_DELAY_MS)
    },
    [cancel],
  )

  const close = useCallback(() => setIsOpen(false), [])

  return { prompt, isOpen, schedule, close }
}
